<template>
  <div>
    <div class="d-flex justify-content-between">
      <div class="label">
        <label>
          {{ $t('text_content.documents.files_count') }}: <span class="font-weight-bold">{{ filesCount }}</span>
        </label>
      </div>
      <div class="d-flex button-group actions-btns">
        <input enctype="multipart/form-data" type="file" ref="selectFiles" @change="loadFiles" multiple>
        <b-button class="btn btn-warning" @click="chooseFiles" size="sm">
          {{ $t('buttons.documents.load_files') }}
        </b-button>
        <b-button class="btn btn-danger" @click="deleteFile" :disabled="!selectedFile" size="sm">
          {{ $t('buttons.documents.delete_file') }}
        </b-button>
        <b-button class="btn btn-success" @click="openSelectedFile" :disabled="!selectedFile" size="sm">
          {{ $t('buttons.documents.open_file') }}
        </b-button>
      </div>
    </div>
    <form ref="fileForm">
      <ul class="files-wrapper" :class="{ dragover: isSomethingDraggedOver }" :style="{height: isCollapse ? 'calc(100vh - 575px)' : 'calc(100vh - 300px)'}">
        <li v-for="item in documents" class="file" :key="item.id" @click="toggleFile(item)"
            :class="{'selected-file': selectedFile && item.id === selectedFile.id}">
          <icon name="file"></icon>
          <span :title="item.filename">{{ item.filename }}</span>
        </li>
      </ul>
    </form>
  </div>
</template>

<script>
import { getFile } from '@/helpers/commonHelpers'
import { saveAs } from 'file-saver'
import { notificationsRenderer } from '@/helpers/renderHelpers'

export default {
  props: ['documents', 'url', 'isCollapse'],
  data () {
    return {
      selectedFile: null,
      dragAndDropCapable: false,
      isSomethingDraggedOver: false
    }
  },
  methods: {
    /**
     * Determines if the drag and drop functionality is in the window
     * @returns {boolean}
     */
    determineDragAndDropCapable () {
      const div = document.createElement('div')

      return (('draggable' in div)
          || ('ondragstart' in div && 'ondrop' in div))
          && 'FormData' in window
          && 'FileReader' in window
    },
    dragFile (e) {
      e.preventDefault()
      e.stopPropagation()
      if (!this.isSomethingDraggedOver) {
        this.isSomethingDraggedOver = true
      }
    },
    dragOff (e) {
      e.preventDefault()
      e.stopPropagation()
      this.isSomethingDraggedOver = false
    },
    initializeDragAndDrop () {
      this.dragAndDropCapable = this.determineDragAndDropCapable()

      if (this.dragAndDropCapable && this.$refs.fileForm) {
        document.addEventListener('drop', this.dragOff, false)
        document.addEventListener('dragend', this.dragOff, false)
        document.addEventListener('dragenter', this.dragFile, false)
        document.addEventListener('dragover', this.dragFile, false)
        document.addEventListener('mouseenter', this.dragOff, false)
        this.$refs.fileForm.addEventListener('drop', async (e) => {
          const files = []
          for (let i = 0; i < e.dataTransfer.files.length; i++) {
            files.push(e.dataTransfer.files[i])
          }
          await this.loadFilesHandler(files)
        })
      }
    },
    // drag and drop section over
    toggleFile (selectedFile) {
      if (this.selectedFile && selectedFile.id === this.selectedFile.id) {
        this.selectedFile = null
      } else {
        this.selectedFile = selectedFile
      }
    },
    chooseFiles () {
      this.$refs['selectFiles'].click()
    },
    async loadFilesHandler (files) {
      if (files.length) {
        for (let i = 0; i < files.length; ++i) {
          const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Content-Disposition': `form-data; filename=${encodeURIComponent(files[i].name)}`
          }
          const data = await getFile(files[i])
          const requestBody = {
            'content': data.base64StringFile,
            'fileType': data.fileType
          }
          try {
            await this.$http.post(this.url, JSON.stringify(requestBody), { headers })
          } catch (e) {
            notificationsRenderer(e.data, 'error')
          }
        }
      }
      await this.$emit('loadDocuments')
      this.isSomethingDraggedOver = false
    },
    async loadFiles () {
      const files = this.$refs['selectFiles'].files
      await this.loadFilesHandler(files)
    },
    async deleteFile () {
      try {
        await this.$http.delete(`${this.url}${this.selectedFile.id}/`)
        await this.$emit('loadDocuments')
        this.$refs['selectFiles'].value = ''
        this.selectedFile = null
      } catch (e) {
        notificationsRenderer(e.data, 'error')
      }
    },
    async openSelectedFile () {
      try {
        const response = await this.$http.get(`${this.url}${this.selectedFile.id}/`)
        const content = response.body.content
        const uri = `data:${response.body['file_type']};base64,${content}`
        const file = await fetch(uri)
        const blob = await file.blob()
        saveAs(blob, response.body['file_name'])
      } catch (e) {
        notificationsRenderer(e.data, 'error')
      }
    }
  },
  computed: {
    filesCount () {
      return this.documents.length
    }
  },
  mounted () {
    this.initializeDragAndDrop()
  }
}
</script>

<style scoped>
.label {
  padding-top: 8px;
}

.actions-btns {
  margin-top: 9px;
}

.files-wrapper {
  min-height: 250px;
  list-style-type: none;
  padding: 6px;
  border: 1px solid grey;
  background-color: #F6F6F6;
}

.files-wrapper.dragover {
  position: relative;
  border: 1px dashed grey;
}

.files-wrapper.dragover:before {
  content: 'Drop files for uploading here';
  display: inline-block;
  position: absolute;
  top: 80px;
  left: calc(50% - 260px);
  font-size: 46px;
  z-index: 2;
}
.files-wrapper.dragover:after {
  content: '';
  display: inline-block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: gray;
  opacity: 0.7;
  z-index: 1;
}

input[type='file'] {
  display: none;
}

.file {
  float: left;
  width: 200px;
  height: 25px;
  line-height: 25px;
  cursor: pointer;
  padding: 0 1em;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.selected-file {
  background-color: #b0e0e6;
  font-weight: bold;
}

</style>



// WEBPACK FOOTER //
// src/components/common/Documents.vue